import React from 'react';
import { Button, Stack } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { getBlob, getStorage, ref } from 'firebase/storage';
import { useDispatch } from 'react-redux';
import { downloadCsv } from '@/components/tables/export/downloadCsv';
import { showToast } from '@/features/toast/toastSlice';
import { downloadBlob } from '@/utils/downloadBlob';
import { useGetUserImportQuery } from '../../api/userImportsApi';
import {
	USER_IMPORT_FIELDS,
	getImportFieldLabel,
	getImportValueText,
} from '../UserImportReview/importFields';
import { USER_IMPORT_ROW_STATUSES } from './ResultsTable/UserImportRowStatusChip';

// Every row's result, with the sheet row number and the new user's id, for the report
const REPORT_COLUMNS = [
	{ field: 'rowNumber', headerName: 'Row' },
	{ field: 'status', headerName: 'Result' },
	{ field: 'email', headerName: 'Email' },
	{ field: 'name', headerName: 'Name' },
	{ field: 'reason', headerName: 'Reason' },
	{ field: 'userId', headerName: 'User id' },
];

const getReportValue = ({ field }, row) =>
	field === 'status' ? USER_IMPORT_ROW_STATUSES[row.status].label : row[field];

// The rows as they were imported: the row number, then each field as spreadsheet text
const IMPORTED_ROW_COLUMNS = [
	{ field: 'rowNumber', headerName: 'Row' },
	...USER_IMPORT_FIELDS.map((field) => ({ field, headerName: getImportFieldLabel(field) })),
];

const getImportedRowValue = ({ field }, { rowNumber, values }) =>
	field === 'rowNumber' ? rowNumber : getImportValueText(field, values[field]);

/**
 * A user import's downloads, from the cached getUserImport:
 * - Download report: every row's result as a CSV (row, result, email, name, reason, user id)
 * - Download imported rows: the rows as the admin reviewed them (the rows file at rowsPath, read
 *   with the Storage SDK as the signed-in admin) as a CSV, one column per field. Only for
 *   imports started from the review.
 * - Download log: the log as text, one line per entry (time, level, message)
 * All are named after the uploaded file.
 */
export const UserImportDownloads = ({ importId }) => {
	const dispatch = useDispatch();
	const { data: userImport } = useGetUserImportQuery(importId);
	const baseName = userImport.fileName.replace(/\.xlsx$/i, '');

	const handleImportedRows = async () => {
		try {
			const blob = await getBlob(ref(getStorage(), userImport.rowsPath));
			const rows = JSON.parse(await blob.text());
			downloadCsv({
				columns: IMPORTED_ROW_COLUMNS,
				rows,
				getValue: getImportedRowValue,
				fileName: `${baseName}_Imported_Rows`,
			});
		} catch (error) {
			console.error('Could not download the imported rows', error);
			dispatch(
				showToast({ message: 'Couldn\'t download the rows. Please try again.', error: true }),
			);
		}
	};

	const handleReport = () =>
		downloadCsv({
			columns: REPORT_COLUMNS,
			rows: userImport.rows,
			getValue: getReportValue,
			fileName: `${baseName}_Import_Report`,
		});

	const handleLog = () =>
		downloadBlob(
			new Blob(
				[
					userImport.log
						.map(({ at, level, message }) => `${at}\t${level.toUpperCase()}\t${message}`)
						.join('\n'),
				],
				{ type: 'text/plain;charset=utf-8;' },
			),
			`${baseName}_Import_Log.txt`,
		);

	return (
		<Stack direction='row' spacing={1}>
			<Button
				variant='outlined'
				startIcon={<DownloadIcon />}
				onClick={handleReport}
				disabled={!userImport.rows.length}
			>
				Download report
			</Button>
			{userImport.rowsPath && (
				<Button variant='outlined' startIcon={<DownloadIcon />} onClick={handleImportedRows}>
					Download imported rows
				</Button>
			)}
			<Button variant='outlined' startIcon={<DownloadIcon />} onClick={handleLog}>
				Download log
			</Button>
		</Stack>
	);
};

UserImportDownloads.displayName = 'UserImportDownloads';
